import React from 'react';
import { VaultKeTheme } from '../../utils/theme';

const Careers: React.FC = () => {
  const styles: { [key: string]: React.CSSProperties } = {
    page: {
      minHeight: '100vh',
      backgroundColor: VaultKeTheme.colors.background,
      color: VaultKeTheme.colors.text,
    },
    main: {
      paddingTop: '80px',
      paddingBottom: VaultKeTheme.spacing.xxl,
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: `0 ${VaultKeTheme.spacing.xl}`,
    },
    header: {
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xxl,
      padding: VaultKeTheme.spacing.xxl,
      background: `linear-gradient(135deg, ${VaultKeTheme.colors.surface} 0%, ${VaultKeTheme.colors.surfaceVariant} 100%)`,
      borderRadius: VaultKeTheme.borderRadius.xl,
    },
    title: {
      ...VaultKeTheme.typography.h1,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    subtitle: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
      maxWidth: '600px',
      margin: '0 auto',
    },
    jobs: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
      gap: VaultKeTheme.spacing.xl,
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    job: {
      ...VaultKeTheme.components.card,
    },
    jobTitle: {
      ...VaultKeTheme.typography.h4,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    jobLocation: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.primary,
      marginBottom: VaultKeTheme.spacing.md,
      fontWeight: '600',
    },
    jobDesc: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
      marginBottom: VaultKeTheme.spacing.lg,
      lineHeight: '1.6',
    },
    jobTags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: VaultKeTheme.spacing.sm,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    tag: {
      ...VaultKeTheme.typography.caption,
      backgroundColor: VaultKeTheme.colors.surfaceVariant,
      color: VaultKeTheme.colors.textSecondary,
      padding: '4px 8px',
      borderRadius: VaultKeTheme.borderRadius.sm,
    },
    applyButton: {
      ...VaultKeTheme.components.button.primary,
      width: '100%',
    },
    culture: {
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    cultureTitle: {
      ...VaultKeTheme.typography.h2,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    cultureText: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
      maxWidth: '600px',
      margin: '0 auto',
    },
    values: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: VaultKeTheme.spacing.xl,
    },
    value: {
      textAlign: 'center',
      ...VaultKeTheme.components.card,
    },
    valueIcon: {
      fontSize: '3rem',
      marginBottom: VaultKeTheme.spacing.md,
    },
    valueTitle: {
      ...VaultKeTheme.typography.h4,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    valueDesc: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
    },
  };

  const jobs = [
    {
      title: 'Senior Frontend Developer',
      location: 'Kisumu, Kenya (Remote)',
      description: 'Build beautiful, responsive web applications using React and TypeScript. Work on our customer-facing products and internal tools.',
      tags: ['React', 'TypeScript', 'CSS', 'JavaScript'],
    },
    {
      title: 'Backend Engineer',
      location: 'Kisumu, Kenya',
      description: 'Design and implement scalable APIs and services using Go. Work with our financial systems and ensure high availability.',
      tags: ['Go', 'PostgreSQL', 'REST APIs', 'Microservices'],
    },
    {
      title: 'Product Manager',
      location: 'Kisumu, Kenya (Hybrid)',
      description: 'Drive product strategy and roadmap. Work closely with engineering, design, and stakeholders to deliver exceptional user experiences.',
      tags: ['Product Strategy', 'Analytics', 'User Research', 'Agile'],
    },
    {
      title: 'UI/UX Designer',
      location: 'Kisumu, Kenya (Remote)',
      description: 'Create intuitive and beautiful user experiences. Design interfaces that make complex financial tools simple and accessible.',
      tags: ['Figma', 'User Research', 'Prototyping', 'Design Systems'],
    },
  ];

  const values = [
    {
      icon: '🌍',
      title: 'Community First',
      description: 'We build for communities and believe in the power of collective action.',
    },
    {
      icon: '🚀',
      title: 'Innovation',
      description: 'We embrace new technologies and approaches to solve real problems.',
    },
    {
      icon: '🤝',
      title: 'Collaboration',
      description: 'We work together across disciplines to achieve extraordinary results.',
    },
    {
      icon: '💎',
      title: 'Excellence',
      description: 'We strive for the highest quality in everything we do.',
    },
  ];

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <div style={styles.container}>
          <div style={styles.header}>
            <h1 style={styles.title}>Join Our Team</h1>
            <p style={styles.subtitle}>
              Help us build the future of community finance. We're looking for passionate
              individuals who want to make a real impact on financial inclusion in Africa.
            </p>
          </div>

          <div style={styles.jobs}>
            {jobs.map((job, index) => (
              <div key={index} style={styles.job}>
                <h3 style={styles.jobTitle}>{job.title}</h3>
                <div style={styles.jobLocation}>{job.location}</div>
                <p style={styles.jobDesc}>{job.description}</p>
                <div style={styles.jobTags}>
                  {job.tags.map((tag, tagIndex) => (
                    <span key={tagIndex} style={styles.tag}>{tag}</span>
                  ))}
                </div>
                <button style={styles.applyButton}>Apply Now</button>
              </div>
            ))}
          </div>

          <div style={styles.culture}>
            <h2 style={styles.cultureTitle}>Our Culture</h2>
            <p style={styles.cultureText}>
              We believe that great companies are built by great teams. Our culture is rooted
              in collaboration, innovation, and a commitment to making a positive impact.
            </p>
          </div>

          <div style={styles.values}>
            {values.map((value, index) => (
              <div key={index} style={styles.value}>
                <div style={styles.valueIcon}>{value.icon}</div>
                <h4 style={styles.valueTitle}>{value.title}</h4>
                <p style={styles.valueDesc}>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Careers;